// IMPORT REACT
import React, { useContext, useEffect, useState } from "react";

// IMPORT UI COMPONENTS
import {
  Box,
  Button,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material";

import logo from "../../assets/images/logo.png";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { PreferencesContext } from "../../PreferenceContext";
import { useQuery } from "../../shared/CommonFunction";
import { useNavigate } from "react-router-dom";
import parse from "html-react-parser";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import dayjs from "dayjs";
import { AlertType } from "../../services/models/AlertType";
import LoadingButton from "@mui/lab/LoadingButton";
import { EncryptDecryptService } from "../../services/common/EncryptionService";
import {
  CreateReferBookEntry,
  GetReferBookTokenDetail,
} from "../../services/ReferBookEntryService";
import { SaveNotificationLog } from "../../services/TokenService";

// COMMON STYLES
const value = {
  color: "#000",
  marginLeft: "4px",
};

const filedsLabelText = {
  fontSize: "14px",
  padding: "0px 0px 0px 5px !important",
};

// SCHEMA FOR OPTION
interface selectOption {
  label: string;
  value: string;
}

interface BranchDetails {
  consumerId: number;
  organizationId: number;
  branchId: number;
  doctorId: number;
  fullName: string;
  qualification: string;
  branchName: string;
  organizationName: string;
  address1: string;
  address2: string;
  city: string;
  postalCode: string;
  email: string;
  mobileNumber: string;
  landLineNumber: string;
}

interface IReasonForReference {
  displayOrder: number;
  reasonForReferenceValue: selectOption | null;
  reasonForReferenceLabel: string;
}

interface ReferBook {
  referBookEntryId: number;
  consumerId: number;
  organizationId: number;
  branchId: number;

  fromDoctorId?: number;
  fromDoctorName?: string;
  fromDoctorQualification?: string;
  fromDoctorContactNumber?: string;

  fromLocationId?: number;
  fromLocationName?: string;
  fromLocationCode?: string;
  fromLocationAddress1?: string;
  fromLocationAddress2?: string;
  fromLocationCity?: string;
  fromLocationCountryId?: number;
  fromLocationCountry?: string;
  fromLocationCountryRegionId?: number;
  fromLocationCountryRegion?: string;
  fromLocationPostalCode?: string;
  fromLocationContactNumber?: string;
  fromLocationEmail?: string;

  referDate?: Date | null;

  toDoctorId?: number;
  toDoctorName?: string;
  toDoctorQualification?: string;

  toLocationId?: number;
  toLocationName?: string;
  toLocationCode?: string;
  toLocationAddress1?: string;
  toLocationAddress2?: string;
  toLocationCity?: string;
  toLocationCountryId?: number;
  toLocationCountry?: string;
  toLocationCountryRegionId?: number;
  toLocationCountryRegion?: string;
  toLocationPostalCode?: string;
  toLocationContactNumber?: string;
  toLocationEmail?: string;

  forPatientId?: number;
  forPatientFirstName?: string;
  forPatientLastName?: string;
  forPatientAgeDay?: number | null;
  forPatientAgeMonth?: number | null;
  forPatientAgeYear?: number | null;
  forPatientBirthDate?: string;
  forPatientGender?: selectOption | null;
  forPatientMobileNumber?: string;

  reasonForReferenceColl: Array<IReasonForReference>;

  clinicalNotes?: string;

  isDeleted?: boolean;
  dataManagedBy?: number;
  mode?: string;
}

const ReferBookPreview = () => {
  const initialBranchDetails: BranchDetails = {
    consumerId: 0,
    organizationId: 0,
    branchId: 0,
    fullName: "",
    qualification: "",
    doctorId: 0,
    branchName: "",
    organizationName: "",
    address1: "",
    address2: "",
    city: "",
    postalCode: "",
    email: "",
    mobileNumber: "",
    landLineNumber: "",
  };

  const initReferBookEntryData: ReferBook = {
    referBookEntryId: 0,
    consumerId: 0,
    organizationId: 0,
    branchId: 0,

    fromDoctorId: 0,
    fromDoctorName: "",
    fromDoctorQualification: "",
    fromDoctorContactNumber: "",
    fromLocationId: 0,
    fromLocationName: "",
    fromLocationCode: "",
    fromLocationAddress1: "",
    fromLocationAddress2: "",
    fromLocationCity: "",
    fromLocationCountryId: 0,
    fromLocationCountry: "",
    fromLocationCountryRegionId: 0,
    fromLocationCountryRegion: "",
    fromLocationPostalCode: "",
    fromLocationContactNumber: "",
    fromLocationEmail: "",

    referDate: new Date(Date.now()) || null,

    toDoctorId: 0,
    toDoctorName: "",
    toDoctorQualification: "",
    toLocationId: 0,
    toLocationName: "",
    toLocationCode: "",
    toLocationAddress1: "",
    toLocationAddress2: "",
    toLocationCity: "",
    toLocationCountryId: 0,
    toLocationCountry: "",
    toLocationCountryRegionId: 0,
    toLocationCountryRegion: "",
    toLocationPostalCode: "",
    toLocationContactNumber: "",
    toLocationEmail: "",

    forPatientId: 0,
    forPatientFirstName: "",
    forPatientLastName: "",
    forPatientAgeDay: null,
    forPatientAgeMonth: null,
    forPatientAgeYear: null,
    forPatientBirthDate: "",
    forPatientGender: null,
    forPatientMobileNumber: "",

    reasonForReferenceColl: [],

    clinicalNotes: "",

    isDeleted: false,
  };

  const navigate = useNavigate();
  const smallScreen = useMediaQuery("(max-width:768px)");
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [branchDetails, setBranchDetails] =
    useState<BranchDetails>(initialBranchDetails);
  const [referBookData, setReferBookData] = useState();
  const edService = new EncryptDecryptService();

  // GETTTING PARAMS
  let urlParams = useQuery();
  let token = urlParams.get("t");
  let currentToken: string = "";

  if (token !== null && token !== undefined) {
    currentToken = token;
  } else {
    navigate("/not-found");
  }

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    referBookEntryId: Yup.number().nullable(),
    consumerId: Yup.number().required(),
    organizationId: Yup.number().required(),
    branchId: Yup.number().required(),

    fromDoctorId: Yup.number().nullable(),
    fromDoctorName: Yup.string().required("Ref By Doctor Name is required"),
    fromDoctorQualification: Yup.string().nullable(),
    fromDoctorContactNumber: Yup.string().required(
      "Contact Number is required"
    ),
    toLocationName: Yup.object()
      .nullable()
      .transform((value) => (value === "" ? null : value))
      .required("Location is required"),
    referDate: Yup.string().required("Refer Date is required"),

    toDoctorId: Yup.number().nullable(),
    toDoctorName: Yup.string().nullable(),
    toDoctorQualification: Yup.string().nullable(),

    forPatientId: Yup.number().nullable(),
    forPatientFirstName: Yup.string().required("First Name is required"),
    forPatientLastName: Yup.string().required("Last Name is required"),
    forPatientGender: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Gender is required"),
    forPatientBirthDate: Yup.string().nullable(),
    forPatientMobileNumber: Yup.string().nullable(),

    clinicalNotes: Yup.string().nullable(),

    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: initReferBookEntryData,
  });

  const getGenderValue = (gender: any) => {
    if (!gender) return "";
    if (typeof gender === "string") return gender;
    return gender?.value || "";
  };

  const getGenderLabel = (gender: any) => {
    if (!gender) return "";
    if (typeof gender === "string") return gender;
    return gender?.label || gender?.value || "";
  };

  const getReferBookTokenDetail = async () => {
    try {
      let tokenData: any = null;
      const params = new URLSearchParams();
      params.append("token", currentToken || "");
      const result: any = await GetReferBookTokenDetail(params, currentToken);

      if (result?.data?.data) {
        const data = result.data.data;

        tokenData = {
          consumerId: data.consumerId ?? 0,
          organizationId: data.organizationId ?? 0,
          branchId: data.branchId ?? 0,
          doctorId: data.doctorId ?? 0,
          fullName: data.fullName ?? "",
          qualification: data.qualification ?? "",
          branchName: data.branchName ?? "",
          organizationName: data.organizationName ?? "",
          address1: data.address1 ?? "",
          address2: data.address2 ?? "",
          city: data.city ?? "",
          postalCode: data.postalCode ?? "",
          email: data.email ?? "",
          mobileNumber: data.mobileNumber ?? "",
          landLineNumber: data.landLineNumber ?? "",
        };

        setBranchDetails(tokenData);
      }
      return tokenData;
    } catch (e) {
      console.error("Error fetching branch details:", e);
    }
  };

  const handleSaveNotificationOnly = async (id: any) => {
    if (!referBookData) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: "Referral data not loaded.",
        alertType: AlertType.Error,
      });
      return;
    }

    const patientName = `${referBookData.forPatientFirstName || ""} ${
      referBookData.forPatientLastName || ""
    }`.trim();
    const doctorName = referBookData.fromDoctorName || "";
    const qualification = referBookData.fromDoctorQualification
      ? ` (${referBookData.fromDoctorQualification})`
      : "";
    const toDoctorName = referBookData.toDoctorName || "";
    const toDoctorQualification = referBookData.toDoctorQualification
      ? ` (${referBookData.toDoctorQualification})`
      : "";

    const message = `${doctorName}${qualification} has referred a patient ${patientName} to ${toDoctorName}${toDoctorQualification}.`;

    const notificationPayload = {
      notificationId: 0,
      consumerId: branchDetails?.consumerId ?? 0,
      orgId: branchDetails?.organizationId ?? 0,
      branchId: branchDetails?.branchId ?? 0,
      specialityId: userContext?.specialityId ?? 0,
      departmentId: userContext?.departmentId ?? 0,

      fromUserId: referBookData?.fromDoctorId ?? 0,
      fromUserFullName: referBookData?.fromDoctorName
        ? `${referBookData.fromDoctorName}${
            referBookData.fromDoctorQualification
              ? ` (${referBookData.fromDoctorQualification})`
              : ""
          }`
        : "",
      fromUserMobileNumber: referBookData?.fromDoctorContactNumber ?? "",
      fromUserEmail: userContext?.loginDetail?.email ?? "",

      notificationData: message,
      mediaType: "WHATSAPP",
      templateFor: "REFER_BOOK",

      linkId: id,
      tableName: "ReferBookEntry",

      isDeleted: false,
      dataManagedBy: userContext?.loginDetail?.userId ?? 0,

      notificationDetailjson: JSON.stringify([
        {
          notificationUserMappingId: 0,
          notificationId: 0,
          toUserId: referBookData?.toDoctorId ?? 0,
          toUserFullName: `${referBookData?.toDoctorName ?? ""}`.trim(),
          toUserEmail: referBookData?.toLocationEmail ?? "",
          toUserMobileNumber: referBookData?.toLocationContactNumber ?? "",
          linkId: id,
          tableName: "ReferBookEntry",
          code: referBookData?.referBookNumber ?? "",
          isVisiable: true,
          isRead: false,
          isDeleted: false,
        },
      ]),
    };

    try {
      await SaveNotificationLog(notificationPayload, currentToken);

      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: "Notification saved successfully!",
        alertType: AlertType.Success,
      });
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: "Failed to save notification.",
        alertType: AlertType.Error,
      });
    }
  };

  const saveReferBookEntry = async (value: ReferBook) => {
    try {
      let result: any = "";
      const reasonForReferenceColl =
        value?.reasonForReferenceColl?.map((item: any, index: number) => {
          return {
            displayOrder: index,
            reasonForReferenceValue: item?.value,
            reasonForReferenceLabel: item?.label,
          };
        }) || [];

      let formData = {
        referBookEntryId: value?.referBookEntryId || 0,

        consumerId: value?.consumerId || 0,
        organizationId: value?.organizationId || 0,
        branchId: value?.branchId || 0,

        fromDoctorId: value?.fromDoctorId || 0,
        fromDoctorName: value?.fromDoctorName || "",
        fromDoctorQualification: value?.fromDoctorQualification || "",
        fromDoctorContactNumber: value?.fromDoctorContactNumber || "",

        fromLocationId: value?.fromLocationId || 0,
        fromLocationName: value?.fromLocationName || "",
        fromLocationCode: value?.fromLocationCode || "",
        fromLocationAddress1: value?.fromLocationAddress1 || "",
        fromLocationAddress2: value?.fromLocationAddress2 || "",
        fromLocationCity: value?.fromLocationCity || "",
        fromLocationCountryId: value?.fromLocationCountryId || 0,
        fromLocationCountry: value?.fromLocationCountry || "",
        fromLocationCountryRegionId: value?.fromLocationCountryRegionId || 0,
        fromLocationCountryRegion: value?.fromLocationCountryRegion || "",
        fromLocationPostalCode: value?.fromLocationPostalCode || "",
        fromLocationContactNumber: value?.fromLocationContactNumber || "",
        fromLocationEmail: value?.fromLocationEmail || "",

        referDate: value?.referDate
          ? dayjs(value?.referDate).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),

        toDoctorId: value?.toDoctorId || 0,
        toDoctorName: value?.toDoctorName || "",
        toDoctorQualification: value?.toDoctorQualification || "",
        toLocationId: value?.toLocationId || 0,
        toLocationName: value?.toLocationName || "",
        toLocationCode: value?.toLocationCode || "",
        toLocationAddress1: value?.toLocationAddress1 || "",
        toLocationAddress2: value?.toLocationAddress2 || "",
        toLocationCity: value?.toLocationCity || "",
        toLocationCountryId: value?.toLocationCountryId || 0,
        toLocationCountry: value?.toLocationCountry || "",
        toLocationCountryRegionId: value?.toLocationCountryRegionId || 0,
        toLocationCountryRegion: value?.toLocationCountryRegion || "",
        toLocationPostalCode: value?.toLocationPostalCode || "",
        toLocationContactNumber: value?.toLocationContactNumber || "",
        toLocationEmail: value?.toLocationEmail || "",

        forPatientId: value?.forPatientId || 0,
        forPatientFirstName: value?.forPatientFirstName || "",
        forPatientLastName: value?.forPatientLastName || "",
        forPatientAgeDay: value?.forPatientAgeDay || 0,
        forPatientAgeMonth: value?.forPatientAgeMonth || 0,
        forPatientAgeYear: value?.forPatientAgeYear || 0,
        forPatientBirthDate: value?.forPatientBirthDate || "",
        forPatientGender: getGenderValue(value.forPatientGender),
        forPatientMobileNumber: value?.forPatientMobileNumber || "",

        reasonForReferenceJson: JSON.stringify(reasonForReferenceColl),
        referBookAvailableDateJson: JSON.stringify(
          referBookData?.referBookDate
        ),
        clinicalNotes: value?.clinicalNotes || "",

        isDeleted: value?.isDeleted || false,
        dataManagedBy: userContext?.loginDetail?.userId || 0,
      };
      result = await CreateReferBookEntry(formData, currentToken);

      if (result?.data) {
        const id = result?.data?.referBookEntryId?.toString() || "";
        await handleSaveNotificationOnly(id);

        const encId = encodeURIComponent(
          edService.encrypt(result?.data?.referBookEntryId?.toString() || "") ||
            ""
        );
        localStorage.removeItem(currentToken);
        navigate(`/refer-book-thank-You?t=${currentToken}&q=${encId}`);
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  const handleSubmit = async () => {
    if (!referBookData) return;
    await saveReferBookEntry(referBookData);
  };

  const fetchData = async () => {
    try {
      await getReferBookTokenDetail();
      const jsonData = localStorage.getItem(currentToken);
      if (!jsonData) {
        navigate("/not-found");
        return;
      }
      const refBook = JSON.parse(jsonData);
      setReferBookData(refBook);
      methods.reset(refBook);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  // HANDLE BACK
  const handleBack = () => {
    navigate(-1);
  };

  // FORMAT TIME UTILITY
  const formatTime = (time: string) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, []);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          minHeight: "100vh",
          paddingY: 6,
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box
          className={"main-container"}
          sx={{
            width: "100%",
            maxWidth: 900,
            backgroundColor: "#fff",
            borderRadius: "10px",
            boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
            p: 3,
            marginX: "auto",
          }}
        >
          <Grid item xs={12} mb={1.5}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "18px !important",
                marginLeft: "4px",
                textAlign: "center",
              }}
            >
              {referBookData?.organizationName}
            </Typography>
          </Grid>

          <Divider sx={{ mb: 1 }} />

          <Grid item xs={12} mb={1.5}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "18px !important",
                marginLeft: "4px",
              }}
            >
              {referBookData?.toDoctorName}{" "}
              {referBookData?.toDoctorQualification && (
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 500,
                    fontSize: "14px !important",
                  }}
                >
                  ({referBookData.toDoctorQualification})
                </Typography>
              )}
            </Typography>
          </Grid>

          <Divider sx={{ mb: 1 }} />

          <Grid item xs={12} mb={1}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                fontSize: "16px !important",
                marginLeft: "4px",
              }}
            >
              {referBookData?.toLocationName}
            </Typography>

            {/* Address Line */}
            <Typography variant="body2" sx={{ ...value, mt: 0.5 }}>
              {[
                referBookData?.toLocationAddress1,
                referBookData?.toLocationAddress2,
                referBookData?.toLocationCity,
                referBookData?.toLocationCountry
                  ? `${referBookData?.toLocationCountry} - ${referBookData?.toLocationPostalCode}`
                  : referBookData?.toLocationPostalCode,
              ]
                .filter(Boolean)
                .join(", ")}
            </Typography>

            {/* Mobile Numbers */}
            {(referBookData?.toLocationContactNumber ||
              referBookData?.fromLocationAlternateContactNumber ||
              referBookData?.toLocationEmail) && (
              <Typography variant="body2" sx={{ ...value, mt: 0.5 }}>
                {[
                  referBookData?.toLocationContactNumber
                    ? `Contact Number: ${referBookData?.toLocationContactNumber}`
                    : "",
                  referBookData?.fromLocationAlternateContactNumber
                    ? `${referBookData?.fromLocationAlternateContactNumber}`
                    : "",
                  referBookData?.toLocationEmail
                    ? `Email: ${referBookData?.toLocationEmail}`
                    : "",
                ]
                  .filter(Boolean)
                  .join(", ")}
              </Typography>
            )}
          </Grid>

          <Divider sx={{ mb: 1 }} />
          {referBookData?.referBookDate?.length > 0 && (
            <Box mt={1}>
              <Typography fontWeight={600} fontSize="14px" mb={0.5}>
                {/* Next available dates: */}
                Next working day/hours:
              </Typography>
              {/* {referBookData?.referBookDate.map((item: any, index: number) => (
                <Typography key={index} fontSize="13px">
                  {item.dayName},{" "}
                  {dayjs(item.appointmentDate).format("MMM DD, YYYY")} -{" "}
                  {formatTime(item.startTime)} to {formatTime(item.endTime)}
                </Typography>
              ))} */}
              {referBookData?.referBookDate.map((item: any, index: number) => (
                <Typography key={index} fontSize="13px">
                  {item.dayName},{" "}
                  {dayjs(item.appointmentDate).format("MMM DD, YYYY")} -{" "}
                  {item.timeSlot}
                </Typography>
              ))}
            </Box>
          )}

          <Divider sx={{ mb: 1, mt: 1 }} />

          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography sx={filedsLabelText}>Ref Date:</Typography>
              <Typography variant="body1" sx={value}>
                {referBookData?.referDate
                  ? dayjs(referBookData.referDate).format("DD-MM-YYYY")
                  : ""}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={1} mt={0.2}>
            <Grid item xs={6}>
              <Typography sx={filedsLabelText}>Patient First Name:</Typography>
              <Typography variant="body1" sx={value}>
                {referBookData?.forPatientFirstName}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography sx={filedsLabelText}>Patient Last Name:</Typography>
              <Typography variant="body1" sx={value}>
                {referBookData?.forPatientLastName}
              </Typography>
            </Grid>

            {(referBookData?.forPatientGender > 0 ||
              referBookData?.forPatientAgeYear > 0 ||
              referBookData?.forPatientAgeMonth > 0 ||
              referBookData?.forPatientAgeDay > 0) && (
              <Grid item xs={6}>
                <Typography sx={filedsLabelText}>GENDER / AGE:</Typography>

                <Typography variant="body1" sx={value}>
                  {getGenderLabel(referBookData?.forPatientGender || null)}
                  {referBookData?.forPatientAgeYear > 0 ||
                  referBookData?.forPatientAgeMonth > 0 ||
                  referBookData?.forPatientAgeDay > 0
                    ? " / "
                    : ""}

                  {referBookData?.forPatientAgeYear > 0
                    ? `${referBookData.forPatientAgeYear}y `
                    : ""}
                  {referBookData?.forPatientAgeMonth > 0
                    ? `${referBookData.forPatientAgeMonth}m `
                    : ""}
                  {referBookData?.forPatientAgeDay > 0
                    ? `${referBookData.forPatientAgeDay}d`
                    : ""}
                </Typography>
              </Grid>
            )}

            {referBookData?.forPatientMobileNumber && (
              <Grid item xs={6}>
                <Typography sx={filedsLabelText}>
                  Patient Mobile Number:
                </Typography>
                <Typography variant="body1" sx={value}>
                  {referBookData?.forPatientMobileNumber}
                </Typography>
              </Grid>
            )}
          </Grid>

          {referBookData?.reasonForReferenceColl?.length > 0 && (
            <Grid item xs={12}>
              <Divider sx={{ mb: 1, mt: 1 }} />
              <Typography sx={filedsLabelText}>
                Reason for Reference:
              </Typography>
              <Typography variant="body1" sx={value}>
                {referBookData?.reasonForReferenceColl
                  .map((item: any) => item?.reasonForReferenceName)
                  .join(", ")}
              </Typography>
            </Grid>
          )}

          {referBookData?.clinicalNotes && (
            <Grid item xs={12}>
              <Divider sx={{ mb: 1, mt: 1 }} />
              <Typography sx={filedsLabelText}>Clinical Notes:</Typography>
              <Typography variant="body1" sx={value}>
                {parse(
                  (referBookData?.clinicalNotes || "")
                    .replace(/^<p>/, "")
                    .replace(/<\/p>$/, "")
                )}
              </Typography>
            </Grid>
          )}

          <Divider sx={{ mb: 1, mt: 1 }} />

          {(referBookData?.fromDoctorName ||
            referBookData?.fromDoctorQualification ||
            referBookData?.fromDoctorContactNumber) && (
            <Grid container spacing={1} mb={2}>
              {referBookData?.fromDoctorName && (
                <Grid item xs={6}>
                  <Typography sx={filedsLabelText}>
                    Ref By Doctor (Your Name):
                  </Typography>
                  <Typography variant="body1" sx={value}>
                    {referBookData?.fromDoctorName}
                  </Typography>
                </Grid>
              )}

              {referBookData?.fromDoctorQualification && (
                <Grid item xs={6}>
                  <Typography sx={filedsLabelText}>Qualification:</Typography>
                  <Typography variant="body1" sx={value}>
                    {referBookData?.fromDoctorQualification}
                  </Typography>
                </Grid>
              )}

              {referBookData?.fromDoctorContactNumber && (
                <Grid item xs={6}>
                  <Typography sx={filedsLabelText}>Contact Number:</Typography>
                  <Typography variant="body1" sx={value}>
                    {referBookData?.fromDoctorContactNumber}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}

          <Grid item xs={12} mt={2}>
            <Box
              className="page-header-icon"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Button
                color="primary"
                variant="outlined"
                className="secondary-button"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
              >
                Back
              </Button>

              <LoadingButton
                color="primary"
                loadingPosition="start"
                startIcon={<></>}
                variant="contained"
                type="submit"
                className="primary-button"
                name="save"
                onClick={handleSubmit}
                disabled={isLoading || methods.formState.isSubmitting}
              >
                Submit
              </LoadingButton>
            </Box>
          </Grid>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            width: "100%",
          }}
        >
          <Typography
            className="font-size-11"
            sx={{ whiteSpace: "nowrap", marginTop: "2px" }}
          >
            Powered by
          </Typography>
          <a href="/" target="_blank" rel="noopener noreferrer">
            <img
              src={logo}
              alt="Logo"
              style={{
                height: 20,
                width: "auto",
                objectFit: "contain",
                verticalAlign: "middle",
                cursor: "pointer",
              }}
            />
          </a>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          pb: 2,
          flexDirection: smallScreen ? "column" : "row",
          gap: smallScreen ? 1 : 0,
        }}
      >
        <Typography className="font-size-11">
          Copyrights &copy; {new Date().getFullYear()} - Pediaverse Solutions
          Pvt Ltd
        </Typography>

        <Typography className="font-size-11">
          <a
            href="/terms"
            target="_blank"
            className="blue-link"
            style={{ textDecoration: "none" }}
          >
            Terms of Service
          </a>{" "}
          &nbsp;
          <a
            href="/privacy-policy"
            target="_blank"
            className="blue-link"
            style={{ textDecoration: "none" }}
          >
            Privacy Policy
          </a>
        </Typography>
      </Box>
    </>
  );
};

export default ReferBookPreview;
